import { useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MessageSquare, X, Send } from "lucide-react";
import { API } from "@/lib/api";
import { useLang } from "@/i18n";

const uid = () => Math.random().toString(36).slice(2) + Date.now().toString(36);

const SUGGESTIONS = {
  it: [
    "Cos'è AIR FORCE ONE?",
    "Differenze tra LIFE e PRO MAX?",
    "Come funziona la prova a casa?",
    "Come richiedo un preventivo?",
  ],
  en: [
    "What is AIR FORCE ONE?",
    "Differences between LIFE and PRO MAX?",
    "How does the home trial work?",
    "How do I request a quote?",
  ],
};

const GREETING = {
  it: "Ciao, sono ARIA — l'assistente virtuale di Aéràs Technology. Posso aiutarti su prodotti, tecnologie e preventivi. Cosa vuoi sapere?",
  en: "Hi, I'm ARIA — the virtual assistant of Aéràs Technology. I can help with products, technologies and quotes. What would you like to know?",
};

export default function ChatWidget() {
  const { lang, tr } = useLang();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const sessionId = useMemo(() => `w-${uid()}`, []);
  const scrollRef = useRef(null);

  const shown = messages.length ? messages : [{ role: "assistant", content: GREETING[lang] }];

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, open]);

  const send = async (text) => {
    const content = (text ?? input).trim();
    if (!content || busy) return;
    setInput("");
    setMessages((m) => [
      ...(m.length ? m : [{ role: "assistant", content: GREETING[lang] }]),
      { role: "user", content },
      { role: "assistant", content: "" },
    ]);
    setBusy(true);

    try {
      const res = await fetch(`${API}/chat/stream`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId, message: content }),
      });
      if (!res.body) throw new Error("no stream");
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let assistant = "";
      let buffer = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });

        // SSE events are separated by a blank line (\n\n). Keep the trailing
        // (possibly incomplete) event in the buffer for the next iteration.
        const events = buffer.split(/\n\n/);
        buffer = events.pop() ?? "";

        for (const evt of events) {
          const lines = evt.split("\n").filter((l) => l.startsWith("data: "));
          if (!lines.length) continue;
          const payload = lines.map((l) => l.slice(6)).join("\n");
          if (payload === "[DONE]") continue;
          if (payload.startsWith("[ERROR]")) throw new Error("stream error");
          assistant += payload;
          setMessages((m) => {
            const copy = [...m];
            copy[copy.length - 1] = { role: "assistant", content: assistant };
            return copy;
          });
        }
      }
    } catch {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = {
          role: "assistant",
          content: tr({
            it: "Ops, si è verificato un errore. Riprova o scrivici a info@aerastechnology.it.",
            en: "Oops, something went wrong. Please retry or write to info@aerastechnology.it.",
          }),
        };
        return copy;
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      {/* FAB */}
      <motion.button
        data-testid="chat-fab"
        initial={{ scale: 0, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ delay: 1.6, duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
        onClick={() => setOpen((v) => !v)}
        className="fixed bottom-6 right-6 z-40 w-14 h-14 flex items-center justify-center glass-strong hover:border-[#007A8A] transition-colors group"
        aria-label="Chat"
      >
        <span className="absolute top-2 right-2 w-2 h-2 bg-[#00E5FF] rounded-full animate-pulse" />
        {open ? <X size={20} /> : <MessageSquare size={20} className="group-hover:text-[#007A8A] transition-colors" />}
      </motion.button>

      <AnimatePresence>
        {open && (
          <motion.aside
            data-testid="chat-panel"
            initial={{ opacity: 0, y: 24, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.98 }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            className="fixed bottom-24 right-6 z-40 w-[min(420px,calc(100vw-3rem))] h-[560px] max-h-[calc(100vh-8rem)] glass-strong flex flex-col overflow-hidden"
          >
            {/* Header */}
            <div className="p-5 border-b border-black/10 flex items-center justify-between">
              <div>
                <div className="font-mono text-[10px] tracking-[0.25em] text-[#007A8A]">
                  ● ARIA — LIVE
                </div>
                <div className="font-display text-xl mt-1">
                  {tr({ it: "Assistente Aéràs", en: "Aéràs Assistant" })}
                </div>
              </div>
              <button onClick={() => setOpen(false)} aria-label="Chiudi" className="p-1 text-[#3f3f46] hover:text-[#0a0a0a]">
                <X size={18} />
              </button>
            </div>

            {/* Messages */}
            <div ref={scrollRef} className="flex-1 overflow-y-auto p-5 space-y-4">
              {shown.map((m, i) => (
                <div
                  key={i}
                  className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
                  data-testid={`chat-msg-${m.role}`}
                >
                  <div
                    className={`max-w-[85%] px-4 py-3 text-sm leading-relaxed font-body whitespace-pre-wrap ${
                      m.role === "user"
                        ? "bg-[#00E5FF] text-black"
                        : "bg-[#ededf0] border border-[#e4e4e7] text-[#0a0a0a]"
                    }`}
                  >
                    {m.content || (
                      <span className="inline-flex gap-1">
                        <span className="w-1.5 h-1.5 bg-black/40 rounded-full animate-pulse" />
                        <span className="w-1.5 h-1.5 bg-black/40 rounded-full animate-pulse" style={{ animationDelay: "0.2s" }} />
                        <span className="w-1.5 h-1.5 bg-black/40 rounded-full animate-pulse" style={{ animationDelay: "0.4s" }} />
                      </span>
                    )}
                  </div>
                </div>
              ))}

              {messages.length <= 1 && (
                <div className="pt-4 space-y-2">
                  <div className="font-mono text-[10px] tracking-[0.2em] text-[#52525b]">
                    {tr({ it: "SUGGERIMENTI", en: "SUGGESTIONS" })}
                  </div>
                  {SUGGESTIONS[lang].map((s) => (
                    <button
                      key={s}
                      data-testid={`chat-suggestion-${s.slice(0, 6)}`}
                      onClick={() => send(s)}
                      className="block text-left w-full text-sm px-3 py-2 border border-[#e4e4e7] hover:border-[#007A8A] hover:text-[#007A8A] transition-colors font-body"
                    >
                      {s}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Input */}
            <form
              onSubmit={(e) => {
                e.preventDefault();
                send();
              }}
              className="p-4 border-t border-black/10 flex gap-2"
              data-testid="chat-form"
            >
              <input
                data-testid="chat-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder={tr({ it: "Scrivi un messaggio…", en: "Type a message…" })}
                className="flex-1 bg-[#f4f4f5] border border-[#e4e4e7] focus:border-[#007A8A] outline-none px-4 py-3 text-sm font-body transition-colors"
              />
              <button
                type="submit"
                disabled={busy || !input.trim()}
                data-testid="chat-send"
                className="px-4 bg-[#00E5FF] text-black disabled:opacity-40 hover:bg-[#0a0a0a] hover:text-white transition-colors"
              >
                <Send size={16} />
              </button>
            </form>
          </motion.aside>
        )}
      </AnimatePresence>
    </>
  );
}
